// Mobile mode utilities.
//
// The app is served at https://mobile.xlibris.app to offer a forced mobile
// experience: on that host the mobile layout applies regardless of the real
// viewport size. This module is the single source of truth for the mobile
// breakpoint and for detecting the forced-mobile host.

export const MOBILE_BREAKPOINT = 768;
export const FORCED_MOBILE_HOSTNAME = 'mobile.xlibris.app';

const getWindowHostname = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location?.hostname ?? '';
};

/**
 * Whether the app is served from the forced-mobile host.
 * @param {string} [hostname] Defaults to the current window hostname
 * @returns {boolean}
 */
export const isForcedMobileHost = (hostname = getWindowHostname()) => {
  return hostname === FORCED_MOBILE_HOSTNAME;
};

/**
 * Whether the app should render the mobile experience, either because the
 * viewport is narrow or because the forced-mobile host is being used.
 * @returns {boolean}
 */
export const isMobileViewport = () => {
  if (isForcedMobileHost()) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const visualViewport = window.visualViewport;
  const viewportWidth = visualViewport?.width ?? window.innerWidth;

  return viewportWidth <= MOBILE_BREAKPOINT;
};

const WIDTH_CONDITION_RE = /\((min|max)-width:\s*([\d.]+)px\)/g;

/**
 * Decide how a media query should behave when the viewport must be treated
 * as mobile-sized (as if its width were capped at MOBILE_BREAKPOINT).
 *
 * @param {string} mediaText The media query text (e.g. "(max-width: 768px)")
 * @returns {'all' | 'not all' | null} The forced media text, or null when the
 *   query's natural behavior already matches the capped-width semantics
 *   (small-phone refinements, non-width queries) and must be left untouched.
 */
export const resolveForcedMediaText = mediaText => {
  if (!mediaText || mediaText.includes(',')) {
    return null;
  }

  const conditions = [...mediaText.matchAll(WIDTH_CONDITION_RE)];
  if (conditions.length === 0) {
    return null;
  }

  let allMaxWidthsCoverBreakpoint = true;

  for (const [, type, value] of conditions) {
    const px = parseFloat(value);

    if (type === 'min') {
      if (px > MOBILE_BREAKPOINT) {
        // Desktop-only rule: must never apply on the forced-mobile host.
        return 'not all';
      }
      // A min-width at or below the breakpoint depends on the real viewport
      // width in the same way with or without the cap.
      return null;
    }

    if (px < MOBILE_BREAKPOINT) {
      allMaxWidthsCoverBreakpoint = false;
    }
  }

  // max-width thresholds below the breakpoint are small-phone refinements
  // that should keep responding to the real viewport width.
  return allMaxWidthsCoverBreakpoint ? 'all' : null;
};

/**
 * Rewrite width-based media queries in every accessible stylesheet so the
 * document renders as a mobile-sized viewport. Cross-origin stylesheets are
 * skipped (their rules are not readable). Idempotent.
 * @param {Document} [doc]
 */
export const forceMobileMediaQueries = (doc = document) => {
  for (const sheet of Array.from(doc.styleSheets)) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      // Cross-origin stylesheet: not rewritable.
      continue;
    }

    if (!rules) {
      continue;
    }

    for (const rule of Array.from(rules)) {
      if (!rule.media) {
        continue;
      }

      const forcedMediaText = resolveForcedMediaText(rule.media.mediaText);
      if (forcedMediaText) {
        rule.media.mediaText = forcedMediaText;
      }
    }
  }
};

/**
 * Enable forced mobile mode when the app is served from the forced-mobile
 * host: tags the document root with a class hook and keeps media queries
 * rewritten as stylesheets are injected (lazy-loaded chunks add styles after
 * bootstrap).
 * @param {Document} [doc]
 * @returns {boolean} Whether forced mobile mode was enabled
 */
export const initializeForcedMobileMode = (doc = document) => {
  if (!isForcedMobileHost()) {
    return false;
  }

  doc.documentElement.classList.add('exl-force-mobile');
  forceMobileMediaQueries(doc);

  const observer = new MutationObserver(() => forceMobileMediaQueries(doc));
  observer.observe(doc.head, { childList: true, subtree: true });

  // External stylesheets finish parsing after insertion; sweep again once
  // everything has loaded.
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => forceMobileMediaQueries(doc));
  }

  return true;
};
